import { useState } from 'react'
import ModalBackdrop from '../ModalBackdrop'
import { putOverride, type OverrideDraft } from '../../api/admin'
import { serverErrorMessage } from '../../api/client'
import type { MatchView, OverrideWriteResult } from '../../adminApi'

/**
 * The override sheet.
 *
 * Uses the app's existing modal shell rather than a new one. The rule that shapes it:
 * **the provider's current value is shown beside every field.** You are always editing against a
 * visible baseline — a form that hides what it is overriding is how a typo becomes a wrong result
 * scored against every user's prediction.
 *
 * The quick actions exist because the cases that actually occur are one click, not six fields: a
 * match abandoned at 70', a fixture the provider left in `unknown`, a score published wrong.
 */

const STATUSES = [
  'scheduled',
  'live',
  'halftime',
  'finished',
  'postponed',
  'cancelled',
  'abandoned',
  'unknown',
] as const

interface Draft {
  status: string | null
  homeScore: number | null
  awayScore: number | null
  reason: string
}

export default function OverrideSheet({
  match,
  onClose,
  onSaved,
}: {
  match: MatchView
  onClose: () => void
  onSaved: (result: OverrideWriteResult) => void
}) {
  const [draft, setDraft] = useState<Draft>({
    status: null,
    homeScore: null,
    awayScore: null,
    reason: '',
  })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const effectiveHome = draft.homeScore ?? match.homeScore ?? 0
  const effectiveAway = draft.awayScore ?? match.awayScore ?? 0
  const effectiveStatus = draft.status ?? match.status

  const save = async (override: Partial<Draft> = {}) => {
    const merged = { ...draft, ...override }
    if (merged.reason.trim() === '') {
      setError('A reason is required — it is what answers "why is there a manual score here" later.')
      return
    }
    setSaving(true)
    setError(null)
    try {
      const payload: OverrideDraft = {
        status: merged.status,
        homeScore: merged.homeScore,
        awayScore: merged.awayScore,
        reason: merged.reason.trim(),
      }
      onSaved(await putOverride(match.id, payload))
    } catch (failure) {
      setError(serverErrorMessage(failure))
    } finally {
      setSaving(false)
    }
  }

  return (
    <ModalBackdrop onClose={onClose}>
      <div className="bo-sheet">
        <p className="bo-sheet__title">
          {match.homeTeamName} v {match.awayTeamName}
        </p>
        <p className="bo-field__baseline">
          {match.id} · GW{match.gameweek} · kickoff {match.kickoffUtc}
        </p>

        {/* Quick actions for the cases that actually occur, so the common path is one click. */}
        <div className="bo-btn-row">
          <button
            type="button"
            className="bo-btn"
            disabled={saving}
            onClick={() =>
              void save({
                status: 'finished',
                homeScore: match.homeScore ?? 0,
                awayScore: match.awayScore ?? 0,
              })
            }
          >
            Mark finished at current score
          </button>
          <button
            type="button"
            className="bo-btn"
            disabled={saving}
            onClick={() => void save({ status: 'postponed' })}
          >
            Void (postponed)
          </button>
          <button
            type="button"
            className="bo-btn"
            disabled={saving}
            onClick={() => void save({ status: 'abandoned' })}
          >
            Abandon
          </button>
        </div>

        <ScoreField
          label="Home score"
          baseline={match.homeScore}
          value={effectiveHome}
          onChange={(homeScore) => setDraft((previous) => ({ ...previous, homeScore }))}
        />
        <ScoreField
          label="Away score"
          baseline={match.awayScore}
          value={effectiveAway}
          onChange={(awayScore) => setDraft((previous) => ({ ...previous, awayScore }))}
        />

        <div className="bo-field">
          <span className="bo-field__label">Status</span>
          <span className="bo-field__baseline">provider: {match.status}</span>
          <select
            className="bo-input"
            value={effectiveStatus}
            onChange={(e) =>
              setDraft((previous) => ({
                ...previous,
                status: e.target.value === match.status ? null : e.target.value,
              }))
            }
          >
            {STATUSES.map((status) => (
              <option key={status} value={status}>
                {status}
              </option>
            ))}
          </select>
        </div>

        {/* A diff-style confirmation: what it is now, what it becomes. */}
        <p className="bo-sheet__diff">
          {match.homeTeamAbbr} {match.homeScore ?? '-'}–{match.awayScore ?? '-'} {match.awayTeamAbbr} (
          {match.status}) → {effectiveHome}–{effectiveAway} ({effectiveStatus})
        </p>

        <label className="bo-field__label" htmlFor="bo-override-reason">
          Reason (required)
        </label>
        <input
          id="bo-override-reason"
          className="bo-input"
          value={draft.reason}
          placeholder="Abandoned at 70', result awarded 2-0"
          onChange={(e) => setDraft((previous) => ({ ...previous, reason: e.target.value }))}
        />

        {error && <p className="bo-error">{error}</p>}

        <div className="bo-btn-row">
          <button type="button" className="bo-btn" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="button" className="bo-btn bo-btn--danger" onClick={() => void save()} disabled={saving}>
            {saving ? 'Saving…' : 'Apply override'}
          </button>
        </div>
      </div>
    </ModalBackdrop>
  )
}

function ScoreField({
  label,
  baseline,
  value,
  onChange,
}: {
  label: string
  baseline: number | null
  value: number
  onChange: (value: number) => void
}) {
  return (
    <div className="bo-field">
      <span className="bo-field__label">{label}</span>
      <span className="bo-field__baseline">provider: {baseline ?? '—'}</span>
      <span className="bo-field__control">
        <button
          type="button"
          className="bo-stepper"
          aria-label={`${label} down`}
          onClick={() => onChange(Math.max(0, value - 1))}
        >
          −
        </button>
        <span className="bo-mono">{value}</span>
        <button
          type="button"
          className="bo-stepper"
          aria-label={`${label} up`}
          // Server-side the ceiling is 20; matching it here means the input never
          // offers a value the server will refuse.
          onClick={() => onChange(Math.min(20, value + 1))}
        >
          +
        </button>
      </span>
    </div>
  )
}
